/**
 * Hook to get the current logged-in user's profile
 * Uses data abstraction pattern: dummy data in Lovable Preview, real GraphQL in production
 */
import { useMemo, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useGetCurrentUserQuery, useGetCurrentUserWithoutRoleQuery, User } from '@/lib/graphql/types-and-hooks';
import { isLovablePreview } from '@/lib/utils/environment';
import { currentUser } from '@/data/dummy';
import { CURRENT_WORKSPACE_ID } from '@/utils/local_storage_keys';
import { useWorkspaceId } from './use-workspace-id';

export const useCurrentUser = (): {
  currentUser: User | undefined;
  loading: boolean;
  error: any;
  refetch: () => void;
} => {
  // Reactive display name for Lovable Preview (persisted via localStorage)
  const [previewDisplayName, setPreviewDisplayName] = useState(() =>
    localStorage.getItem('coffee-preview-display-name') || currentUser.name
  );

  // Listen for display name changes in preview mode
  useEffect(() => {
    const handler = () => {
      const stored = localStorage.getItem('coffee-preview-display-name');
      if (stored !== null && stored !== previewDisplayName) {
        setPreviewDisplayName(stored);
      }
    };
    window.addEventListener('storage', handler);
    // Also listen for custom event for same-tab updates
    window.addEventListener('preview-display-name-changed', handler);
    return () => {
      window.removeEventListener('storage', handler);
      window.removeEventListener('preview-display-name-changed', handler);
    };
  }, [previewDisplayName]);

  // Dummy data for Lovable Preview
  const dummyUser = useMemo(() => ({
    __typename: 'User' as const,
    id: '1',
    givenName: 'Doug',
    preferredName: previewDisplayName,
    surname: 'Smith',
    email: currentUser.email,
    profileImageUrl: currentUser.avatar,
    lastLogin: new Date().toISOString(),
    isOnboarded: true,
    isSuperuser: false,
    avatarBackground: null,
    defaultSummaryTemplateId: null,
    defaultBriefTemplateId: null,
    role: 'owner', // Owner role for preview mode
    workspaces: [
      {
        __typename: 'Workspace' as const,
        id: '1',
        name: 'Gazillion',
        logoUrl: '/logos/gazillion-icon.jpg',
        domain: 'gazillion.com',
        isMember: true,
      }
    ],
    oauthPlatform: 'google' as const,
  }), [previewDisplayName]);

  // Use Auth0 SDK's reactive isAuthenticated state instead of polling localStorage
  const { isAuthenticated: auth0Authenticated } = useAuth0();
  const hasAuthToken = isLovablePreview() || auth0Authenticated;

  // Step 2: Get workspace ID reactively (updates when workspace changes via event or localStorage)
  const storedWorkspaceId = useWorkspaceId();
  const hasStoredWorkspace = !!storedWorkspaceId;

  // Step 2: Fetch user WITHOUT role (only needed on first login when no stored workspace)
  const {
    data: userWithoutRole,
    loading: loadingWithoutRole,
    error: errorWithoutRole,
    refetch: refetchWithoutRole,
  } = useGetCurrentUserWithoutRoleQuery({
    skip: isLovablePreview() || !hasAuthToken || hasStoredWorkspace,
  });

  const bootstrapWorkspaces = (userWithoutRole?.me?.workspaces?.filter(Boolean) ?? []) as Array<{ id: string }>;
  const firstBootstrapWorkspaceId = bootstrapWorkspaces[0]?.id;

  // Step 3: Determine workspace ID for role query
  // Fast path: use stored workspace directly (skips the without-role query entirely)
  // Slow path: wait for without-role query to discover available workspaces
  const workspaceIdForRoleQuery = hasStoredWorkspace
    ? storedWorkspaceId
    : firstBootstrapWorkspaceId;

  // Step 4: Fetch user WITH role using determined workspace ID
  const {
    data,
    loading: loadingWithRole,
    error: errorWithRole,
    refetch: refetchWithRole,
  } = useGetCurrentUserQuery({
    variables: { workspaceId: workspaceIdForRoleQuery || '' },
    skip: isLovablePreview() || !workspaceIdForRoleQuery || !hasAuthToken,
  });

  // Workspaces from whichever query has resolved (with-role also returns workspaces)
  const workspaces = ((data?.me?.workspaces ?? userWithoutRole?.me?.workspaces)?.filter(Boolean) ?? []) as Array<{ id: string }>;
  const firstWorkspaceId = workspaces[0]?.id;
  const storedWorkspaceIsValid =
    !!storedWorkspaceId && workspaces.some((w) => w.id === storedWorkspaceId);

  // Step 5: Validate and store workspace ID
  useEffect(() => {
    if (!hasAuthToken) return;
    if (isLovablePreview()) return;
    if (!workspaces.length) return;

    // If the stored workspace is invalid but we do have accessible workspaces,
    // auto-switch to the first available workspace.
    if (storedWorkspaceId && !storedWorkspaceIsValid && firstWorkspaceId) {
      localStorage.setItem(CURRENT_WORKSPACE_ID, firstWorkspaceId);
      window.dispatchEvent(
        new CustomEvent('workspaceChanged', { detail: { workspaceId: firstWorkspaceId } })
      );
      return;
    }

    // Initialize storage if empty.
    if (!storedWorkspaceId && firstWorkspaceId) {
      localStorage.setItem(CURRENT_WORKSPACE_ID, firstWorkspaceId);
      window.dispatchEvent(
        new CustomEvent('workspaceChanged', { detail: { workspaceId: firstWorkspaceId } })
      );
    }
  }, [
    hasAuthToken,
    storedWorkspaceId,
    storedWorkspaceIsValid,
    firstWorkspaceId,
    workspaces.length,
  ]);

  // In Lovable Preview, return dummy data
  if (isLovablePreview()) {
    return {
      currentUser: dummyUser as User,
      loading: false,
      error: undefined,
      refetch: () => {},
    };
  }

  // In production, return real GraphQL data
  // Loading is true if either query is loading
  const loading = loadingWithoutRole || loadingWithRole;

  return {
    currentUser: (data?.me || userWithoutRole?.me) as User | undefined,
    loading,
    // The without-role query is the one a brand-new user (no stored workspace)
    // depends on — its failure must surface so callers can offer a retry
    // instead of rendering a dataless shell.
    error: errorWithRole || errorWithoutRole,
    // Retry whichever query is active for the current workspace state.
    refetch: hasStoredWorkspace ? refetchWithRole : refetchWithoutRole,
  };
};

// Auth0's normalized profile sets `nickname` to the email local-part when the IdP
// (e.g. Google) doesn't supply one. That value lands in our `preferred_name` column
// and would otherwise shadow the real `given_name`. Ignore it here so display falls
// through to `givenName`.
const cleanPreferredName = (user: Pick<User, 'preferredName' | 'email'>): string | undefined => {
  const pn = user.preferredName?.trim();
  if (!pn) return undefined;
  const localPart = user.email?.split('@')[0]?.trim().toLowerCase();
  if (localPart && pn.toLowerCase() === localPart) return undefined;
  return pn;
};

/**
 * Helper to get user's display name
 */
export const getUserDisplayName = (user: User | undefined): string => {
  if (!user) return 'User';

  const preferred = cleanPreferredName(user);
  if (preferred) return preferred;
  if (user.givenName) return user.givenName;
  return user.email?.split('@')[0] || 'User';
};

/**
 * Helper to get user's full name
 */
export const getUserFullName = (user: User | undefined): string => {
  if (!user) return 'User';

  const firstName = cleanPreferredName(user) || user.givenName || '';
  const lastName = user.surname || '';

  if (firstName && lastName) return `${firstName} ${lastName}`;
  if (firstName) return firstName;
  if (lastName) return lastName;
  return user.email?.split('@')[0] || 'User';
};
